import React from 'react';

interface PageWrapperProps {
  // PageHeader関連のProps
  title: string;
  subtitle?: string;
  icon?: string;
  actions?: React.ReactNode;
  titleClassName?: string;
  
  // PageContainer関連のProps
  children: React.ReactNode;
  className?: string;
  fluid?: boolean;
  padding?: string;
  pageType?: 'default' | 'background-image' | 'admin';
  
  // 追加オプション
  headerClassName?: string;
  showHeader?: boolean;
}

/**
 * ページラッパーコンポーネント
 * PageContainer + PageHeader を統合し、ページレイアウトを一元管理
 * 
 * @param title - ページタイトル（必須）
 * @param subtitle - ページサブタイトル
 * @param icon - タイトル横に表示するアイコン
 * @param actions - ヘッダー右側に表示するアクション要素
 * @param titleClassName - タイトルのCSSクラス
 * @param children - ページコンテンツ
 * @param className - コンテナの追加CSSクラス
 * @param fluid - fluid container を使用するか
 * @param padding - コンテナのパディング
 * @param pageType - ページタイプ（レイアウト調整用）
 * @param headerClassName - ヘッダー部分の追加CSSクラス
 * @param showHeader - ヘッダーを表示するか（デフォルト: true）
 */
const PageWrapper: React.FC<PageWrapperProps> = ({ 
  // PageHeader props
  title,
  subtitle,
  icon,
  actions,
  titleClassName = 'zen-old-mincho-bold',
  
  // PageContainer props
  children,
  className = '',
  fluid = true,
  padding = 'p-5',
  pageType = 'default',
  
  // 追加オプション
  headerClassName = '',
  showHeader = true
}) => {
  // コンテナクラスの決定
  const containerClass = fluid ? 'container-fluid' : 'container';
  
  // ページタイプに応じたパディング設定
  let finalPadding = padding;
  if (pageType === 'background-image') {
    finalPadding = 'p-0';
  } else if (pageType === 'admin') {
    finalPadding = 'p-5';
  } else if (pageType === 'default') {
    finalPadding = 'p-5';
  }
  
  return (
    <div className={`${containerClass} ${finalPadding} ${className}`}>
      <div className="row">
        <div className="col-12">
          {/* ページヘッダー */}
          {showHeader && (
            <div className={`d-flex justify-content-between align-items-center mb-4 ${headerClassName}`}>
              <div>
                <h3 className={`mb-1 ${titleClassName}`}>
                  {icon && <i className={`${icon} me-2`}></i>}
                  {title}
                </h3>
                {subtitle && (
                  <p className="text-muted mb-0">{subtitle}</p>
                )}
              </div>
              {actions && (
                <div className="d-flex align-items-center gap-2">
                  {actions}
                </div>
              )}
            </div>
          )}
          
          {/* ページコンテンツ */}
          {children}
        </div>
      </div>
    </div>
  );
};

export default PageWrapper; 